import React, { useMemo, useState } from "react";
import { AlertCircle, ArrowLeft, ArrowRight, Mail, Phone, User } from "lucide-react";
import LocationSelect from "../components/LocationSelect";

export const GoogleCompleteProfilePage = ({
  initialData,
  onBack,
  onComplete,
}) => {
  const role = String(initialData?.role || "lawyer").toLowerCase() === "lawyer" ? "lawyer" : "client";
  const [formData, setFormData] = useState({
    name: initialData?.name || "",
    email: initialData?.email || "",
    phone: "",
    state: "",
    city: "",
    pincode: "",
    enrollmentNumber: "",
    password: "",
  });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const title = useMemo(
    () => (role === "lawyer" ? "Complete Lawyer Profile" : "Complete Client Profile"),
    [role]
  );

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    const cleanedPhone = String(formData.phone || "").replace(/\D/g, "");
    if (!formData.name.trim() || !formData.email.trim() || !cleanedPhone || !formData.city.trim() || !formData.pincode.trim()) {
      setError("Please fill all required fields.");
      return;
    }
    if (!/^[6-9]\d{9}$/.test(cleanedPhone)) {
      setError("Enter a valid 10-digit Indian mobile number.");
      return;
    }
    if (!/^\d{6}$/.test(String(formData.pincode || ""))) {
      setError("Enter a valid 6-digit pincode.");
      return;
    }
    if (role === "lawyer" && !String(formData.enrollmentNumber || "").trim()) {
      setError("Enrollment number is required for lawyers.");
      return;
    }

    try {
      setLoading(true);
      await onComplete({
        idToken: initialData?.idToken,
        googleAuth: true,
        role,
        email: formData.email.trim().toLowerCase(),
        name: formData.name.trim(),
        phone: cleanedPhone,
        state: formData.state.trim(),
        city: formData.city.trim(),
        pincode: formData.pincode.trim(),
        enrollmentNumber: formData.enrollmentNumber.trim(),
        password: formData.password,
      });
    } catch (err) {
      setError(err?.response?.data?.message || err?.message || "Failed to complete profile.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#f1f4f9] flex items-center justify-center p-4">
      <div className="w-full max-w-2xl bg-white rounded-2xl shadow-lg p-6 sm:p-8">
        <button
          type="button"
          onClick={onBack}
          className="inline-flex items-center gap-1.5 text-sm text-slate-600 font-semibold"
        >
          <ArrowLeft size={14} /> Back
        </button>

        <h2 className="text-3xl sm:text-4xl font-black text-slate-900 mt-4">{title}</h2>
        <p className="text-slate-600 mt-2">Signed in with Google. Please complete your profile.</p>

        {error && (
          <div className="mt-4 rounded-xl border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700 flex items-center gap-2">
            <AlertCircle size={14} /> {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="sm:col-span-2">
            <label className="block text-sm font-semibold text-slate-900 mb-2">Full Name</label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"><User size={16} /></span>
              <input
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                className="w-full h-12 pl-10 pr-3 rounded-xl border border-slate-300"
                placeholder="Enter your full name"
              />
            </div>
          </div>

          <div className="sm:col-span-2">
            <label className="block text-sm font-semibold text-slate-900 mb-2">Email</label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"><Mail size={16} /></span>
              <input
                name="email"
                value={formData.email}
                readOnly
                className="w-full h-12 pl-10 pr-3 rounded-xl border border-slate-300 bg-slate-100 text-slate-600"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-semibold text-slate-900 mb-2">Phone</label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"><Phone size={16} /></span>
              <input
                name="phone"
                value={formData.phone}
                onChange={handleInputChange}
                className="w-full h-12 pl-10 pr-3 rounded-xl border border-slate-300"
                placeholder="9876543210"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-semibold text-slate-900 mb-2">Pincode</label>
            <input
              name="pincode"
              value={formData.pincode}
              onChange={handleInputChange}
              className="w-full h-12 px-3 rounded-xl border border-slate-300"
              placeholder="800001"
            />
          </div>

          <div className="sm:col-span-2">
            <label className="block text-sm font-semibold text-slate-900 mb-2">Location</label>
            <LocationSelect
              state={formData.state}
              city={formData.city}
              onStateChange={(val) => setFormData(prev => ({ ...prev, state: val, city: '' }))}
              onCityChange={(val) => setFormData(prev => ({ ...prev, city: val }))}
              labelState="State"
              labelCity="District"
              required
            />
          </div>

          {role === "lawyer" && (
            <div className="sm:col-span-2">
              <label className="block text-sm font-semibold text-slate-900 mb-2">Enrollment Number</label>
              <input
                name="enrollmentNumber"
                value={formData.enrollmentNumber}
                onChange={handleInputChange}
                className="w-full h-12 px-3 rounded-xl border border-slate-300"
                placeholder="BAR/1234"
              />
            </div>
          )}

          <div className="sm:col-span-2">
            <label className="block text-sm font-semibold text-slate-900 mb-2">Password (optional for Google)</label>
            <input
              name="password"
              type="password"
              value={formData.password}
              onChange={handleInputChange}
              className="w-full h-12 px-3 rounded-xl border border-slate-300"
              placeholder="Optional"
            />
          </div>

          <div className="sm:col-span-2">
            <button
              type="submit"
              disabled={loading}
              className="w-full h-12 rounded-xl bg-black text-white font-semibold inline-flex items-center justify-center gap-2 disabled:opacity-60"
            >
              {loading ? "Saving..." : <>Complete Profile <ArrowRight size={16} /></>}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
